import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Paperclip } from "lucide-react";
import { useClients } from "@/api/clients";
import {
  useAcceptSuggestion,
  useDismissSuggestion,
  type AcceptEventInput,
  type AcceptInput,
  type NewCallPayload,
  type Suggestion,
} from "@/api/mail";
import { EVENT_CHOICES, type EventKind } from "@/api/operations";
import { useVessels } from "@/api/vessels";
import { NewCallForm } from "@/components/mail/NewCallForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type Mode = "idle" | "accept" | "dismiss";

const CONFIDENCE: Record<Suggestion["confidence"], "success" | "warning" | "muted"> = {
  high: "success",
  medium: "warning",
  low: "muted",
};

export function eventLabel(kind: string): string {
  return EVENT_CHOICES.find((choice) => choice.value === kind)?.label ?? kind;
}

/** The proposal in plain words. */
export function describe(suggestion: Suggestion): string {
  const p = suggestion.payload;
  switch (suggestion.kind) {
    case "new_vessel":
      return `New vessel ${String(p.name)}${p.port ? ` at ${String(p.port)}` : ""}. Register it?`;
    case "new_call": {
      const call = p as NewCallPayload;
      const where = call.port ? ` at ${call.terminal || call.port}` : "";
      const when = call.eta ? `, ETA ${call.eta}` : "";
      return `New call: ${call.vessel_name}${where}${when}. Create the call and its draft invoice?`;
    }
    case "call_event":
      return `This looks like "${eventLabel(String(p.event_kind))}". Record it?`;
    case "quotation_request":
      return "Someone is asking for a quotation.";
    default:
      return "Not matched to a call. File it by hand, or ignore it.";
  }
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function EventForm({
  initialKind,
  initialAt,
  onSubmit,
  onCancel,
}: {
  initialKind: string;
  initialAt: string;
  onSubmit: (input: AcceptEventInput) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const { values, set } = useFormValues({
    kind: initialKind,
    at: toLocalInput(initialAt),
    note: "",
  });
  const { error, errors, saving, run } = useSubmitState();
  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        kind: values.kind as EventKind,
        happened_at: new Date(values.at).toISOString(),
        note: values.note.trim(),
      }),
    );
  }
  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <RequestError error={error} />
      <div className="grid gap-3 md:grid-cols-3">
        <SelectField
          id={`${id}-kind`}
          label="What happened"
          value={values.kind}
          onChange={(v) => set("kind", v)}
          options={EVENT_CHOICES}
          error={errors.kind}
        />
        <TextField
          id={`${id}-at`}
          label="When"
          type="datetime-local"
          value={values.at}
          onChange={(v) => set("at", v)}
          error={errors.happened_at}
          hint="The e-mail's time, unless you know better"
        />
        <TextField
          id={`${id}-note`}
          label="Note"
          value={values.note}
          onChange={(v) => set("note", v)}
          error={errors.note}
        />
      </div>
      <FormActions saving={saving} submitLabel="Record event" onCancel={onCancel} />
    </form>
  );
}

function VesselForm({
  name,
  onSubmit,
  onCancel,
}: {
  name: string;
  onSubmit: (input: { name: string; imo: string }) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const { values, set } = useFormValues({ name, imo: "" });
  const { error, errors, saving, run } = useSubmitState();
  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit({ name: values.name.trim(), imo: values.imo.trim() }));
  }
  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <RequestError error={error} />
      <div className="grid gap-3 md:grid-cols-2">
        <TextField
          id={`${id}-name`}
          label="Vessel name"
          value={values.name}
          onChange={(v) => set("name", v)}
          error={errors.name}
        />
        <TextField
          id={`${id}-imo`}
          label="IMO number"
          value={values.imo}
          onChange={(v) => set("imo", v)}
          error={errors.imo}
          hint="Seven digits; the e-mail did not say"
        />
      </div>
      <FormActions saving={saving} submitLabel="Register vessel" onCancel={onCancel} />
    </form>
  );
}

function DismissForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (reason: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const [reason, setReason] = useState("");
  const { error, saving, run } = useSubmitState();
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void run(() => onSubmit(reason.trim()));
      }}
      noValidate
      className="flex flex-col gap-3"
    >
      <RequestError error={error} />
      <TextField
        id={`${id}-reason`}
        label="Why dismiss it?"
        value={reason}
        onChange={setReason}
        hint="Optional"
      />
      <FormActions saving={saving} submitLabel="Dismiss" onCancel={onCancel} />
    </form>
  );
}

export function SuggestionCard({ suggestion }: { suggestion: Suggestion }) {
  const [mode, setMode] = useState<Mode>("idle");
  const accept = useAcceptSuggestion();
  const dismiss = useDismissSuggestion();
  const vessels = useVessels("", false);
  const clients = useClients("", false);
  const message = suggestion.message;
  const canAccept = suggestion.kind !== "link";

  async function acceptWith(input: AcceptInput): Promise<void> {
    await accept.mutateAsync({ id: suggestion.id, input });
    setMode("idle");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <span className="truncate">{message.subject || "(no subject)"}</span>
          <Badge variant={CONFIDENCE[suggestion.confidence]}>{suggestion.confidence}</Badge>
          {message.attachment_count > 0 && (
            <Badge variant="secondary">
              <Paperclip className="h-3 w-3" aria-hidden /> {message.attachment_count}
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {message.from_name || message.from_addr} · {new Date(message.sent_at).toLocaleString()} ·{" "}
          <Link
            to={`/mail?tab=recent&message=${message.id}`}
            className="text-primary hover:underline"
          >
            open
          </Link>
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm">{describe(suggestion)}</p>
        {mode === "idle" && (
          <div className="flex flex-wrap gap-2">
            {canAccept && suggestion.kind === "quotation_request" && (
              <Button size="sm" disabled={accept.isPending} onClick={() => void acceptWith({})}>
                Noted
              </Button>
            )}
            {canAccept && suggestion.kind !== "quotation_request" && (
              <Button size="sm" onClick={() => setMode("accept")}>
                Accept
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setMode("dismiss")}>
              Dismiss
            </Button>
          </div>
        )}
        {mode === "accept" && suggestion.kind === "call_event" && (
          <EventForm
            initialKind={String(suggestion.payload.event_kind)}
            initialAt={String(suggestion.payload.happened_at)}
            onSubmit={acceptWith}
            onCancel={() => setMode("idle")}
          />
        )}
        {mode === "accept" && suggestion.kind === "new_vessel" && (
          <VesselForm
            name={String(suggestion.payload.name)}
            onSubmit={acceptWith}
            onCancel={() => setMode("idle")}
          />
        )}
        {mode === "accept" && suggestion.kind === "new_call" && vessels.data && clients.data && (
          <NewCallForm
            payload={suggestion.payload as unknown as NewCallPayload}
            vessels={vessels.data.map((v) => ({ id: v.id, name: v.name }))}
            clients={clients.data.map((c) => ({ id: c.id, code: c.code }))}
            onSubmit={acceptWith}
            onCancel={() => setMode("idle")}
          />
        )}
        {mode === "idle" && <RequestError error={accept.error} />}
        {mode === "dismiss" && (
          <DismissForm
            onSubmit={async (reason) => {
              await dismiss.mutateAsync({ id: suggestion.id, reason });
              setMode("idle");
            }}
            onCancel={() => setMode("idle")}
          />
        )}
      </CardContent>
    </Card>
  );
}
